import { useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { q } from '../api/queries';
import { AuthLayout } from '@bounded/ui/auth-layout';
import { Button, Field, Input } from '@bounded/ui/primitives';
import { ErrorNotice } from '@bounded/ui/state';
import { useT } from '../i18n';
import { safeNext } from './sign-in';

export const Route = createFileRoute('/sign-up')({
  validateSearch: (search): { next?: string } => ({ next: safeNext(search.next) }),
  component: SignUp,
});

const MIN_PASSWORD = 12;

function SignUp() {
  const t = useT(); const m = t.auth.signUp;
  const { next } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const capabilities = useQuery(q.capabilities());
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const signUp = useMutation({
    // With email verification on, sign-up does not create a session; the link brings the user back to sign in.
    mutationFn: () => api.signUp({ name, email, password, callbackURL: '/sign-in?notice=verified' }),
    onSuccess: async () => {
      if (capabilities.data?.email_verification_required) return navigate({ to: '/sign-in', search: { notice: 'check-email', next } });
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      return navigate({ href: next ?? '/' });
    },
  });

  if (capabilities.data && !capabilities.data.signup) {
    return <AuthLayout title={m.closedTitle}><p className="text-graphite">{m.closedBody}</p><Link to="/sign-in" className="text-boundary hover:underline">{m.signIn}</Link></AuthLayout>;
  }
  return (
    <AuthLayout title={m.title} footer={<p>{m.haveAccount} <Link to="/sign-in" search={{ next }} className="text-boundary hover:underline">{m.signIn}</Link></p>}>
      <form className="grid gap-5" onSubmit={(e) => { e.preventDefault(); signUp.mutate(); }}>
        {signUp.error && <ErrorNotice error={signUp.error} onRetry={() => signUp.mutate()} />}
        <Field label={m.name} htmlFor="name"><Input id="name" autoComplete="name" required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label={m.email} htmlFor="email" hint={capabilities.data?.email_verification_required ? m.emailHint : undefined}>
          <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label={m.password} htmlFor="password" hint={m.passwordHint(MIN_PASSWORD, password ? MIN_PASSWORD - password.length : 0)}>
          <Input id="password" type="password" autoComplete="new-password" required minLength={MIN_PASSWORD} value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <Button type="submit" variant="primary" disabled={signUp.isPending || password.length < MIN_PASSWORD}>{signUp.isPending ? m.submitting : m.submit}</Button>
      </form>
    </AuthLayout>
  );
}
